// FileTravailAmo — T-367 (Q-2) : « qu'est-ce qui manque dans chaque dossier ? » Une vue de la grille des dossiers (OHGrid, comme HubGrid),
// alimentée par `GET /api/metier/amo/file-travail` (T-366 : l'étape est calculée par le code, jamais par l'IA). Lecture seule : aucun envoi, aucune écriture.
import { useState, useEffect, useCallback, useMemo } from "react";
import { Eye, Download, AlertTriangle, FileText, Upload, Banknote, Landmark, ClipboardCheck, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { OHGrid } from "../../components/ui/oh-grid";
import { getAuthHeaders } from "./dossier/helpers";

const API = process.env.REACT_APP_BACKEND_URL;

// étape (calculée côté serveur) → puce, couleur de la palette `--oh-stat-*` (paires fond/texte dual-mode), libellé
export const ETAPES_FILE = [
  { key: "incomplet", label: "Incomplet", color: "red", icon: AlertTriangle },
  { key: "dal_a_faire", label: "DAL à faire", color: "orange", icon: FileText },
  { key: "depot_a_faire", label: "Dépôt à faire", color: "blue", icon: Upload },
  { key: "avance_a_faire", label: "Avance à faire", color: "green", icon: Banknote },
  { key: "solde_a_faire", label: "Solde à faire", color: "purple", icon: Landmark },
];
const ETAPE_ATTENTE = { key: "attente", label: "En attente", color: "gray", icon: Hourglass };
const LIBELLE = Object.fromEntries([...ETAPES_FILE.map((e) => [e.key, e.label]), [ETAPE_ATTENTE.key, ETAPE_ATTENTE.label]]);

// onglet cible (donné par le serveur) → onglet de la fiche dossier (DossierDetail.TABS)
export const ONGLET_FICHE = { conformite: "conformite", dal: "dal", mpa: "operation", operation: "operation", commercial: "infos" };
export const ongletFiche = (onglet) => ONGLET_FICHE[onglet] || "infos";

// T-367c : le BLOC à ouvrir dans la fiche (le serveur donne l'étape ; l'écran n'en calcule aucune). Identifiants des panneaux de la fiche dossier.
export const BLOC_PAR_ETAPE = {
  dal_a_faire: "dal-matthieu",                    // DAL › Rédiger avec Matthieu
  depot_a_faire: "preparer-depot-panel",          // Conformité › Préparer le dépôt
};
export const blocFiche = (row) => {
  if (BLOC_PAR_ETAPE[row?.etape]) return BLOC_PAR_ETAPE[row.etape];
  return row?.etape === "incomplet" && row?.onglet === "conformite" ? "conformité-editable" : null;   // Conformité › Vérification (pièces manquantes)
};

const csv = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const PAGE_FILE = 500;   // maximum accepté par la route

export default function FileTravailAmo({ tenantId, onOpenDossier }) {
  const [data, setData] = useState({ lignes: [], compteurs: {}, total_actifs: 0 });
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      // T-367d : la route plafonne une page à 500 lignes (200 par défaut) ; on lit TOUTES les pages, sinon des dossiers manquent à l'écran
      // et les puces (calculées sur tout) ne font plus la somme de la liste.
      let data = null;
      for (let saut = 0; ; saut += PAGE_FILE) {
        const res = await fetch(`${API}/api/metier/amo/file-travail?limite=${PAGE_FILE}&saut=${saut}`, { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (!res.ok) throw new Error(res.status === 403 ? "La file de travail AMO n'est pas disponible pour cet espace." : `Chargement impossible (${res.status})`);
        const page = await res.json();
        data = data ? { ...data, lignes: [...data.lignes, ...(page.lignes || [])] } : { ...page, lignes: page.lignes || [] };
        if (!(page.lignes || []).length || data.lignes.length >= (page.total_filtre ?? data.lignes.length)) break;
      }
      setData(data);
    } catch (e) {
      setErreur(e.message || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  useEffect(() => { charger(); }, [charger]);

  const ouvrir = useCallback((row) => { if (onOpenDossier && row.dossier_id) onOpenDossier(row.dossier_id, ongletFiche(row.onglet), blocFiche(row)); }, [onOpenDossier]);

  // T-367c : par défaut la liste ne montre QUE les dossiers où il y a quelque chose à faire ; ceux « en attente » (rien à faire pour l'instant) sont derrière
  // leur propre puce. Ainsi les puces font la somme de ce qui est affiché.
  const [puceActive, setPuceActive] = useState(null);
  const toutes = useMemo(() => (data.lignes || []).map((l) => ({ ...l, id: l.dossier_id, etape_libelle: LIBELLE[l.etape] || l.etape })), [data.lignes]);
  const lignes = useMemo(() => (puceActive === "attente" ? toutes : toutes.filter((l) => l.etape !== "attente")), [toutes, puceActive]);
  const nbAttente = data.compteurs?.attente ?? 0;
  const nbATraiter = Math.max((data.total_actifs || 0) - nbAttente, 0);

  const stats = useMemo(() => [...ETAPES_FILE, ETAPE_ATTENTE].map((e) => ({
    key: e.key, label: e.label, icon: e.icon, color: e.color, value: data.compteurs?.[e.key] ?? 0, filter: (row) => row.etape === e.key,
  })), [data.compteurs]);

  const columns = useMemo(() => [
    { key: "reference", label: "Réf", mono: true, sortable: true },
    { key: "client", label: "Client", sortable: true },
    { key: "etape_libelle", label: "Étape", sortable: true,
      render: (v, row) => {
        const e = ETAPES_FILE.find((x) => x.key === row.etape);
        const c = e?.color || "gray";
        return <span data-testid={`file-etape-${row.dossier_id}`} style={{ whiteSpace: "nowrap", padding: "2px 8px", borderRadius: 8, fontSize: 12, fontWeight: 500, background: `var(--oh-stat-${c}-bg, var(--oh-bg-elevated))`, color: `var(--oh-stat-${c}-text, var(--oh-text-muted))`, border: `0.5px solid var(--oh-stat-${c}-border, var(--oh-border))` }}>{v}</span>;
      } },
    { key: "motif", label: "Motif", sortable: false, render: (v) => <span style={{ color: "var(--oh-text-secondary)" }}>{v || "—"}</span> },
    { key: "visiteur", label: "Visiteur", sortable: true },
    { key: "anciennete_jours", label: "Ancienneté", sortable: true, sortValue: (r) => r.anciennete_jours ?? -1, render: (v) => (v == null ? "—" : `${v} j`) },
  ], []);

  const rowActions = useMemo(() => [
    { label: "Ouvrir sur l'étape", icon: Eye, onClick: ouvrir },
  ], [ouvrir]);

  const bulkActions = useMemo(() => [{
    label: "Exporter la sélection (CSV)", icon: Download,
    onClick: (ids) => {
      const choisies = lignes.filter((l) => ids.includes(l.id));
      const texte = ["Réf;Client;Étape;Motif;Visiteur;Ancienneté (j)", ...choisies.map((l) => [l.reference, l.client, l.etape_libelle, l.motif, l.visiteur, l.anciennete_jours].map(csv).join(";"))].join("\n");
      const url = URL.createObjectURL(new Blob(["﻿" + texte], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = "file-de-travail-amo.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${choisies.length} ligne${choisies.length > 1 ? "s" : ""} exportée${choisies.length > 1 ? "s" : ""}`);
    },
  }], [lignes]);

  const searchFilter = useCallback((row, q) => [row.reference, row.client, row.motif, row.visiteur, row.etape_libelle].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase())), []);

  return (
    <div className="space-y-4" data-testid="file-travail-amo">
      <div>
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)", margin: 0 }}><ClipboardCheck className="w-5 h-5 inline mr-2" aria-hidden="true" />File de travail AMO</h2>
        <p className="text-sm" style={{ color: "var(--oh-text-secondary)", margin: "4px 0 0" }}>
          <span data-testid="file-travail-resume">
            {nbATraiter === 0 ? "Aucun dossier ne demande d'action pour l'instant." : `${nbATraiter} dossier${nbATraiter > 1 ? "s" : ""} demande${nbATraiter > 1 ? "nt" : ""} une action.`}
            {nbAttente > 0 ? ` ${nbAttente} autre${nbAttente > 1 ? "s sont" : " est"} en attente (rien à faire pour l'instant) : cliquez « En attente » pour les voir.` : ""}
          </span>{" "}
          Les plus anciens sont en premier. Cliquez une ligne : le dossier s'ouvre sur ce qui est à faire.
        </p>
      </div>
      {erreur && <div role="alert" data-testid="file-travail-erreur" className="text-sm p-3 rounded-lg" style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "0.5px solid var(--oh-error-border)" }}>{erreur}</div>}
      <OHGrid
        data={lignes} columns={columns} stats={stats} rowActions={rowActions} bulkActions={bulkActions}
        selectable pagination pageSize={25} loading={loading} onRefresh={charger} onRowClick={ouvrir}
        searchPlaceholder="Rechercher par réf, client, motif, visiteur..." searchFilter={searchFilter}
        defaultSortKey="anciennete_jours" defaultSortDir="desc" onActiveStatChange={setPuceActive}
        emptyMessage="Aucun dossier à traiter" testIdPrefix="file-travail"
      />
    </div>
  );
}
